import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { inventoryApi, reportsApi, storesApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { useModuleScopedBreadcrumbs, useModuleSharedPath } from '@/hooks/useModuleScopedBreadcrumbs';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { InventoryReadinessPanel } from '@/components/org/InventoryReadinessPanel';
import { ProductSelect, StoreSelect } from '@/components/selects';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePermissions } from '@/hooks/usePermissions';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import { useProductOptions } from '@/hooks/useProductOptions';
import { useStoreOptions } from '@/hooks/useStoreOptions';
import type { InventoryResponse, StockTransactionResponse } from '@/types/inventory';
import type {
  InventoryBackorderResponse,
  InventoryReadinessBundleResponse,
  InventorySerialResponse,
  InventoryTransferResponse,
} from '@/types/inventoryPlatform';
import type { StoreResponse } from '@/types/store';
import { formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';
type Tab = 'stock' | 'movements' | 'transfers' | 'readiness' | 'backorders' | 'serials';

const TX_LABELS: Record<string, string> = {
  ADJUSTMENT_IN: 'Adjustment (+)',
  ADJUSTMENT_OUT: 'Adjustment (−)',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  PURCHASE: 'Purchase',
  SALE: 'Sale',
  RETURN: 'Return',
};

export function InventoryPage() {
  const breadcrumbs = useModuleScopedBreadcrumbs('Inventory');
  const ledgerPath = useModuleSharedPath('inventory/ledger');
  const { has } = usePermissions();
  const canApproveTransfer = has('INVENTORY_APPROVE') || has('INVENTORY_ADMIN');
  const canManage = has('INVENTORY_MANAGE');
  const canAdjust = canManage || has('INVENTORY_ADJUST');
  const canTransferOps = has('INVENTORY_MANAGE') || has('INVENTORY_TRANSFER');
  const canExportReports = has('REPORT_VIEW');
  const storeId = useAppSelector((s) => s.app.selectedStore?.id);
  const { getLabel: getProductLabel } = useProductOptions();
  const { getLabel: getStoreLabel } = useStoreOptions();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [tab, setTab] = useState<Tab>('stock');
  const [data, setData] = useState<InventoryResponse[]>([]);
  const [transactions, setTransactions] = useState<StockTransactionResponse[]>([]);
  const [stores, setStores] = useState<StoreResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [selectedStockId, setSelectedStockId] = useState('');
  const [adjustDirection, setAdjustDirection] = useState<'IN' | 'OUT'>('IN');
  const [quantity, setQuantity] = useState('1');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [toStoreId, setToStoreId] = useState('');
  const [transferQty, setTransferQty] = useState('1');
  const [transferBatch, setTransferBatch] = useState('');
  const [transferExpiry, setTransferExpiry] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [transfers, setTransfers] = useState<InventoryTransferResponse[]>([]);
  const [transferRequestOpen, setTransferRequestOpen] = useState(false);
  const [trToStoreId, setTrToStoreId] = useState('');
  const [trProductId, setTrProductId] = useState('');
  const [trQty, setTrQty] = useState('1');
  const [trNotes, setTrNotes] = useState('');
  const [readiness, setReadiness] = useState<InventoryReadinessBundleResponse | null>(null);
  const [backorders, setBackorders] = useState<InventoryBackorderResponse[]>([]);
  const [serials, setSerials] = useState<InventorySerialResponse[]>([]);
  const [serialOpen, setSerialOpen] = useState(false);
  const [serialProductId, setSerialProductId] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [serialImei, setSerialImei] = useState('');

  useEffect(() => {
    storesApi.list({ size: 100, activeOnly: true }).then((p) => setStores(p.content)).catch(() => {});
  }, []);

  const selectedStock = data.find((r) => String(r.id) === selectedStockId);

  const stockOptions = data.map((r) => ({
    value: String(r.id),
    label: `${r.productName ?? 'Product'} (${r.productSku ?? r.productId}) — Qty ${r.quantity}${
      r.batchNumber ? ` · ${r.batchNumber}` : ''
    }`,
  }));

  const applyStockSelection = (stockRowId: string) => {
    setSelectedStockId(stockRowId);
    const row = data.find((r) => String(r.id) === stockRowId);
    if (row) {
      setBatchNumber(row.batchNumber ?? '');
      setExpiryDate(row.expiryDate?.slice(0, 10) ?? '');
      setTransferBatch(row.batchNumber ?? '');
      setTransferExpiry(row.expiryDate?.slice(0, 10) ?? '');
    }
  };

  const loadStock = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      const res = await inventoryApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
        storeId,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [storeId, pagination.page, pagination.size, debouncedSearch, pagination]);

  const loadTransactions = useCallback(
    async (types?: string[]) => {
      if (!storeId) return;
      setLoading(true);
      try {
        const res = await inventoryApi.listTransactions({
          page: pagination.page,
          size: pagination.size,
          storeId,
          transactionTypes: types,
        });
        setTransactions(res.content);
        pagination.applyPageResponse(res);
      } catch (err) {
        showApiError(err, 'Failed to load stock movements');
      } finally {
        setLoading(false);
      }
    },
    [storeId, pagination.page, pagination.size, pagination],
  );

  const loadReadiness = useCallback(async () => {
    setLoading(true);
    try {
      const bundle = await inventoryApi.readinessBundle();
      setReadiness(bundle);
    } catch (err) {
      showApiError(err, 'Failed to load readiness reports');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTransferRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryApi.listTransfers({
        page: pagination.page,
        size: pagination.size,
        fromStoreId: storeId ?? undefined,
      });
      setTransfers(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load transfer requests');
    } finally {
      setLoading(false);
    }
  }, [storeId, pagination.page, pagination.size, pagination]);

  const loadSerials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryApi.listSerials({
        page: pagination.page,
        size: pagination.size,
      });
      setSerials(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load serial numbers');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  const loadBackorders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryApi.listBackorders({
        page: pagination.page,
        size: pagination.size,
        storeId: storeId ?? undefined,
      });
      setBackorders(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load backorders');
    } finally {
      setLoading(false);
    }
  }, [storeId, pagination.page, pagination.size, pagination]);

  useEffect(() => {
    if (tab === 'stock') loadStock();
    else if (tab === 'transfers') loadTransferRequests();
    else if (tab === 'backorders') loadBackorders();
    else if (tab === 'serials') loadSerials();
    else if (tab === 'readiness') loadReadiness();
    else loadTransactions(undefined);
  }, [tab, loadStock, loadTransferRequests, loadBackorders, loadSerials, loadReadiness, loadTransactions]);

  const registerSerial = async (e: FormEvent) => {
    e.preventDefault();
    if (!serialProductId || !serialNumber.trim()) {
      notify.error('Product and serial number required');
      return;
    }
    setSaving(true);
    try {
      await inventoryApi.registerSerial({
        productId: Number(serialProductId),
        serialNumber: serialNumber.trim(),
        imei: serialImei.trim() || undefined,
        statusCode: 'AVAILABLE',
      });
      notify.success('Serial registered');
      setSerialOpen(false);
      setSerialNumber('');
      setSerialImei('');
      loadSerials();
    } catch (err) {
      showApiError(err, 'Register serial failed');
    } finally {
      setSaving(false);
    }
  };

  const exportInventoryReport = async (format: 'csv' | 'excel' | 'pdf') => {
    setExporting(true);
    try {
      await reportsApi.exportInventory(format, storeId ?? undefined);
      notify.success(`Inventory report exported (${format})`);
    } catch (err) {
      showApiError(err, 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const TRANSFER_STATUS_LABEL: Record<string, string> = {
    DRAFT: 'Draft',
    PENDING_APPROVAL: 'Pending approval',
    APPROVED: 'Approved',
    IN_TRANSIT: 'In transit',
    RECEIVED: 'Received',
    CANCELLED: 'Cancelled',
  };

  const runTransferAction = async (
    id: number,
    action: 'submit' | 'approve' | 'dispatch' | 'receive' | 'cancel',
  ) => {
    setSaving(true);
    try {
      const fn = {
        submit: inventoryApi.submitTransfer,
        approve: inventoryApi.approveTransfer,
        dispatch: inventoryApi.dispatchTransfer,
        receive: inventoryApi.receiveTransfer,
        cancel: inventoryApi.cancelTransfer,
      }[action];
      await fn(id);
      notify.success(`Transfer ${action}d`);
      loadTransferRequests();
      if (tab === 'stock') loadStock();
    } catch (err) {
      showApiError(err, `Transfer ${action} failed`);
    } finally {
      setSaving(false);
    }
  };

  const transferLine = (productId: number, qty: string | number) => ({
    productId,
    quantity: Number(qty),
    batchNumber: (transferBatch || selectedStock?.batchNumber)?.trim() || undefined,
  });

  const createTransferRequest = async () => {
    if (!storeId || !trToStoreId || !trProductId) {
      notify.error('Select destination store and product');
      return;
    }
    setSaving(true);
    try {
      await inventoryApi.createTransfer({
        fromStoreId: storeId,
        toStoreId: Number(trToStoreId),
        notes: trNotes.trim() || undefined,
        lines: [transferLine(Number(trProductId), trQty)],
      });
      notify.success('Transfer request created (draft)');
      setTransferRequestOpen(false);
      setTrToStoreId('');
      setTrProductId('');
      setTrQty('1');
      setTrNotes('');
      setTab('transfers');
      loadTransferRequests();
    } catch (err) {
      showApiError(err, 'Create transfer request failed');
    } finally {
      setSaving(false);
    }
  };

  const adjust = async () => {
    if (!storeId || !selectedStock) return;
    const qty = Math.abs(Number(quantity));
    if (!qty) {
      notify.error('Enter a valid quantity');
      return;
    }
    setSaving(true);
    try {
      await inventoryApi.adjust({
        storeId,
        productId: selectedStock.productId,
        quantity: qty,
        transactionType: adjustDirection === 'IN' ? 'ADJUSTMENT_IN' : 'ADJUSTMENT_OUT',
        batchNumber: batchNumber.trim() || undefined,
        expiryDate: expiryDate || undefined,
        notes: adjustNotes.trim() || undefined,
      });
      notify.success('Stock adjusted — tracked in Movements');
      setAdjustOpen(false);
      if (tab === 'stock') loadStock();
      else loadTransactions(undefined);
    } catch (err) {
      showApiError(err, 'Adjust failed');
    } finally {
      setSaving(false);
    }
  };

  const transfer = async () => {
    if (!storeId || !toStoreId || !selectedStock) return;
    setSaving(true);
    try {
      const created = await inventoryApi.createTransfer({
        fromStoreId: storeId,
        toStoreId: Number(toStoreId),
        notes: transferNotes.trim() || undefined,
        lines: [transferLine(selectedStock.productId, transferQty)],
      });
      await inventoryApi.submitTransfer(created.id);
      if (canApproveTransfer) {
        await inventoryApi.approveTransfer(created.id);
        await inventoryApi.dispatchTransfer(created.id);
        await inventoryApi.receiveTransfer(created.id);
        notify.success('Transfer completed via workflow');
      } else {
        notify.success('Transfer submitted for approval');
        setTab('transfers');
      }
      setTransferOpen(false);
      if (tab === 'stock') loadStock();
      else if (tab === 'transfers') loadTransferRequests();
    } catch (err) {
      showApiError(err, 'Transfer failed');
    } finally {
      setSaving(false);
    }
  };

  const allocateBackorder = async (id: number) => {
    setSaving(true);
    try {
      await inventoryApi.allocateBackorder(id);
      notify.success('Backorder allocated — stock reserved');
      loadBackorders();
    } catch (err) {
      showApiError(err, 'Allocate backorder failed');
    } finally {
      setSaving(false);
    }
  };

  const serialColumns: Column<InventorySerialResponse>[] = [
    { key: 'serialNumber', header: 'Serial #' },
    {
      key: 'productId',
      header: 'Product',
      render: (r) => getProductLabel(r.productId, true),
    },
    { key: 'statusCode', header: 'Status' },
    { key: 'imei', header: 'IMEI', render: (r) => r.imei ?? '—' },
    {
      key: 'invoice',
      header: 'Sold on invoice',
      render: (r) => (r.invoiceId != null ? `#${r.invoiceId}` : '—'),
    },
    {
      key: 'warranty',
      header: 'Warranty end',
      render: (r) => (r.warrantyEnd ? formatDate(r.warrantyEnd) : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) =>
        canAdjust && r.statusCode === 'AVAILABLE' ? (
          <Button
            className="!px-2 !py-1 text-xs"
            variant="ghost"
            onClick={async () => {
              try {
                await inventoryApi.updateSerialStatus(r.id, 'RETIRED');
                notify.success('Serial retired');
                loadSerials();
              } catch (err) {
                showApiError(err, 'Update failed');
              }
            }}
          >
            Retire
          </Button>
        ) : null,
    },
  ];

  const stockColumns: Column<InventoryResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (r) => (
        <div>
          <p className="font-medium">{r.productName ?? getProductLabel(r.productId, true)}</p>
          <p className="text-xs text-slate-500">{r.productSku}</p>
        </div>
      ),
    },
    { key: 'quantity', header: 'Qty' },
    { key: 'batchNumber', header: 'Batch', render: (r) => r.batchNumber ?? '—' },
    { key: 'expiryDate', header: 'Expiry', render: (r) => (r.expiryDate ? formatDate(r.expiryDate) : '—') },
  ];

  const txColumns: Column<StockTransactionResponse>[] = [
    {
      key: 'when',
      header: 'Date',
      render: (r) => (r.createdAt ? formatDate(r.createdAt) : '—'),
    },
    {
      key: 'type',
      header: 'Type',
      render: (r) => TX_LABELS[r.transactionType] ?? r.transactionType,
    },
    {
      key: 'product',
      header: 'Product',
      render: (r) => (
        <div>
          <p className="font-medium">{r.productName ?? r.productId}</p>
          <p className="text-xs text-slate-500">{r.productSku}</p>
        </div>
      ),
    },
    { key: 'quantity', header: 'Qty' },
    {
      key: 'store',
      header: 'Store / Route',
      render: (r) => {
        if (r.transactionType === 'TRANSFER_OUT') {
          return `${r.storeName ?? r.storeId} → ${r.relatedStoreName ?? r.referenceId ?? '?'}`;
        }
        if (r.transactionType === 'TRANSFER_IN') {
          return `${r.relatedStoreName ?? r.referenceId ?? '?'} → ${r.storeName ?? r.storeId}`;
        }
        return r.storeName ?? String(r.storeId);
      },
    },
    { key: 'batchNumber', header: 'Batch', render: (r) => r.batchNumber ?? '—' },
    { key: 'expiryDate', header: 'Expiry', render: (r) => (r.expiryDate ? formatDate(r.expiryDate) : '—') },
    { key: 'notes', header: 'Notes', render: (r) => r.notes ?? '—' },
  ];

  const transferColumns: Column<InventoryTransferResponse>[] = [
    { key: 'transferCode', header: 'Code' },
    {
      key: 'route',
      header: 'Route',
      render: (r) => {
        const from = stores.find((s) => s.id === r.fromStoreId)?.name ?? r.fromStoreId;
        const to = stores.find((s) => s.id === r.toStoreId)?.name ?? r.toStoreId;
        return `${from} → ${to}`;
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => TRANSFER_STATUS_LABEL[r.statusCode] ?? r.statusCode,
    },
    {
      key: 'lines',
      header: 'Lines',
      render: (r) =>
        r.lines?.map((l) => `${l.productName ?? getProductLabel(l.productId, true)} × ${l.quantity}`).join(', ') ?? '—',
    },
    {
      key: 'created',
      header: 'Created',
      render: (r) => (r.createdAt ? formatDate(r.createdAt) : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.statusCode === 'DRAFT' && canTransferOps && (
            <>
              <Button className="!px-2 !py-1 text-xs" variant="secondary" onClick={() => runTransferAction(r.id, 'submit')}>
                Submit
              </Button>
              <Button className="!px-2 !py-1 text-xs" variant="ghost" onClick={() => runTransferAction(r.id, 'cancel')}>
                Cancel
              </Button>
            </>
          )}
          {r.statusCode === 'PENDING_APPROVAL' && canTransferOps && (
            <>
              {canApproveTransfer && (
                <Button className="!px-2 !py-1 text-xs" onClick={() => runTransferAction(r.id, 'approve')}>
                  Approve
                </Button>
              )}
              <Button className="!px-2 !py-1 text-xs" variant="ghost" onClick={() => runTransferAction(r.id, 'cancel')}>
                Cancel
              </Button>
            </>
          )}
          {r.statusCode === 'APPROVED' && canTransferOps && (
            <>
              <Button className="!px-2 !py-1 text-xs" onClick={() => runTransferAction(r.id, 'dispatch')}>
                Dispatch
              </Button>
              <Button className="!px-2 !py-1 text-xs" variant="ghost" onClick={() => runTransferAction(r.id, 'cancel')}>
                Cancel
              </Button>
            </>
          )}
          {r.statusCode === 'IN_TRANSIT' && canTransferOps && (
            <Button className="!px-2 !py-1 text-xs" onClick={() => runTransferAction(r.id, 'receive')}>
              Receive
            </Button>
          )}
        </div>
      ),
    },
  ];

  const backorderColumns: Column<InventoryBackorderResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (r) => (
        <div>
          <p className="font-medium">{r.productName ?? getProductLabel(r.productId, true)}</p>
          <p className="text-xs text-slate-500">{r.productSku}</p>
        </div>
      ),
    },
    { key: 'storeName', header: 'Store', render: (r) => r.storeName ?? getStoreLabel(r.storeId, true) },
    { key: 'allocatedQuantity', header: 'Qty' },
    {
      key: 'reference',
      header: 'Reference',
      render: (r) => `${r.referenceType} #${r.referenceId}`,
    },
    {
      key: 'created',
      header: 'Created',
      render: (r) => (r.createdAt ? formatDate(r.createdAt) : '—'),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) =>
        canManage ? (
          <Button className="!px-2 !py-1 text-xs" loading={saving} onClick={() => allocateBackorder(r.id)}>
            Allocate
          </Button>
        ) : null,
    },
  ];

  const tabBtn = (id: Tab, label: string) => (
    <button
      type="button"
      className={`rounded-lg px-4 py-2 text-sm font-medium ${
        tab === id
          ? 'bg-primary-600 text-white'
          : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
      }`}
      onClick={() => {
        setTab(id);
        pagination.resetPage();
      }}
    >
      {label}
    </button>
  );

  const currentStore = stores.find((s) => s.id === storeId);

  return (
    <div>
      <PageHeader
        title="Inventory"
        breadcrumbs={breadcrumbs}
        actions={
          <>
            <Link to={ledgerPath} className="btn btn-secondary btn-sm">
              Ledger
            </Link>
            {tab === 'stock' && canAdjust && (
              <Button onClick={() => setAdjustOpen(true)} disabled={!storeId}>
                Adjust Stock
              </Button>
            )}
            {tab === 'stock' && canManage && (
              <Button variant="secondary" onClick={() => setTransferOpen(true)} disabled={!storeId}>
                Transfer
              </Button>
            )}
            {tab === 'transfers' && canManage && (
              <Button onClick={() => setTransferRequestOpen(true)} disabled={!storeId}>
                New transfer request
              </Button>
            )}
            {tab === 'readiness' && (
              <>
                <Button variant="secondary" onClick={() => loadReadiness()} loading={loading}>
                  Refresh
                </Button>
                {canExportReports ? (
                  <>
                    <Button variant="secondary" loading={exporting} onClick={() => void exportInventoryReport('pdf')}>
                      Export PDF
                    </Button>
                    <Button variant="secondary" loading={exporting} onClick={() => void exportInventoryReport('excel')}>
                      Export Excel
                    </Button>
                    <Button variant="secondary" loading={exporting} onClick={() => void exportInventoryReport('csv')}>
                      Export CSV
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-slate-500">Report export requires REPORT_VIEW permission</span>
                )}
              </>
            )}
            {tab === 'serials' && canAdjust && (
              <Button onClick={() => setSerialOpen(true)}>Register serial</Button>
            )}
          </>
        }
      />

      {!storeId && (
        <p className="mb-4 text-amber-600 dark:text-amber-400">Select a store from the header.</p>
      )}

      <div className="card mb-4 p-4 text-sm text-slate-600 dark:text-slate-400">
        <p className="font-medium text-slate-800 dark:text-slate-200">How inventory works</p>
        <ul className="mt-2 list-inside list-disc space-y-1 text-xs">
          <li>
            <strong>Stock</strong> — on-hand qty per product, batch, and expiry (separate rows per batch).
          </li>
          <li>
            <strong>Adjust</strong> — correction in/out; logged as ADJUSTMENT_IN / ADJUSTMENT_OUT in Movements.
          </li>
          <li>
            <strong>Transfer</strong> — workflow only: create → submit → approve → dispatch → receive (auto-completes if you can approve).
          </li>
          <li>
            <strong>Held bills (POS)</strong> — reserves stock until checkout or restore.
          </li>
          <li>
            <strong>Procurement GRN</strong> — canonical stock-in (legacy purchase create is disabled).
          </li>
          <li>
            <strong>Serials</strong> — register units; POS checkout passes <code>serialIds</code> per line.
          </li>
          <li>
            <strong>Sales</strong> — POS checkout deducts stock automatically (SALE transactions in Movements).
          </li>
        </ul>
        {currentStore && (
          <p className="mt-2 text-xs">
            Current store: <strong>{currentStore.name}</strong>
          </p>
        )}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {tabBtn('stock', 'Stock')}
        {tabBtn('movements', 'Movements')}
        {tabBtn('transfers', 'Transfer requests')}
        {tabBtn('backorders', 'Backorders')}
        {tabBtn('serials', 'Serial numbers')}
        {tabBtn('readiness', 'Readiness')}
      </div>

      {tab === 'stock' && (
        <DataTable columns={stockColumns}
          data={data}
          keyField="id"
          loading={loading}
          search={pagination.search}
          onSearchChange={(v) => {
            pagination.setSearch(v);
            pagination.resetPage();
          }}
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
        />
      )}

      {tab === 'movements' && (
        <DataTable columns={txColumns}
          data={transactions}
          keyField="id"
          loading={loading}
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
        />
      )}

      {tab === 'transfers' && (
        <DataTable columns={transferColumns}
          data={transfers}
          keyField="id"
          loading={loading}
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
        />
      )}

      {tab === 'backorders' && (
        <DataTable columns={backorderColumns}
          data={backorders}
          keyField="id"
          loading={loading}
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
        />
      )}

      {tab === 'serials' && (
        <DataTable columns={serialColumns}
          data={serials}
          keyField="id"
          loading={loading}
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
        />
      )}

      {tab === 'readiness' && readiness && !loading && <InventoryReadinessPanel bundle={readiness} />}
      {tab === 'readiness' && loading && (
        <p className="text-sm text-slate-500">Running inventory integrity and readiness checks…</p>
      )}
      {tab === 'readiness' && !loading && !readiness && (
        <p className="text-sm text-slate-500">Open this tab or click Refresh to run enterprise readiness checks.</p>
      )}

      <Modal
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        title="Adjust stock"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdjustOpen(false)}>
              Cancel
            </Button>
            <Button onClick={adjust} loading={saving}>
              Apply adjustment
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Select
            label="Stock line *"
            placeholder="Select product / batch row"
            options={stockOptions}
            value={selectedStockId}
            onChange={applyStockSelection}
          />
          <Select
            label="Direction *"
            options={[
              { value: 'IN', label: 'Increase stock (ADJUSTMENT_IN)' },
              { value: 'OUT', label: 'Decrease stock (ADJUSTMENT_OUT)' },
            ]}
            value={adjustDirection}
            onChange={(v) => setAdjustDirection(v as 'IN' | 'OUT')}
          />
          <Input
            label="Quantity *"
            type="number"
            min={0.001}
            step="any"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
          <Input
            label="Batch number"
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            placeholder="Optional — use for batch-tracked products"
          />
          <DatePicker
            label="Expiry date"
            value={expiryDate}
            onChange={setExpiryDate}
          />
          <Input
            label="Reason / notes"
            value={adjustNotes}
            onChange={(e) => setAdjustNotes(e.target.value)}
            placeholder="e.g. Damaged goods, stock count correction"
          />
        </div>
      </Modal>

      <Modal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        title="Transfer stock"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setTransferOpen(false)}>
              Cancel
            </Button>
            <Button onClick={transfer} loading={saving}>
              {canApproveTransfer ? 'Transfer (workflow)' : 'Submit transfer'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            From: <strong>{currentStore?.name ?? storeId}</strong> — uses transfer workflow
            {canApproveTransfer ? ' (auto-approve, dispatch & receive)' : ' (pending approval if required)'}.
          </p>
          <Select
            label="Stock line to transfer *"
            placeholder="Select product / batch"
            options={stockOptions}
            value={selectedStockId}
            onChange={applyStockSelection}
          />
          <StoreSelect
            label="To store *"
            placeholder="Select destination"
            value={toStoreId}
            onChange={setToStoreId}
          />
          <Input
            label="Quantity *"
            type="number"
            min={0.001}
            step="any"
            value={transferQty}
            onChange={(e) => setTransferQty(e.target.value)}
          />
          <Input
            label="Batch number"
            value={transferBatch}
            onChange={(e) => setTransferBatch(e.target.value)}
          />
          <DatePicker
            label="Expiry date"
            value={transferExpiry}
            onChange={setTransferExpiry}
          />
          <Input
            label="Notes"
            value={transferNotes}
            onChange={(e) => setTransferNotes(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={transferRequestOpen}
        onClose={() => setTransferRequestOpen(false)}
        title="New transfer request"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setTransferRequestOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createTransferRequest} loading={saving}>
              Create draft
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            From: <strong>{currentStore?.name ?? storeId}</strong> — submit → approve → dispatch → receive.
          </p>
          <StoreSelect
            label="To store *"
            placeholder="Destination"
            value={trToStoreId}
            onChange={setTrToStoreId}
          />
          <ProductSelect label="Product *" value={trProductId} onChange={setTrProductId} required />
          <Input
            label="Quantity *"
            type="number"
            min={0.001}
            step="any"
            value={trQty}
            onChange={(e) => setTrQty(e.target.value)}
          />
          <Input label="Notes" value={trNotes} onChange={(e) => setTrNotes(e.target.value)} />
        </div>
      </Modal>

      <Modal
        open={serialOpen}
        onClose={() => setSerialOpen(false)}
        title="Register serial number"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSerialOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="register-serial-form" loading={saving}>
              Register
            </Button>
          </>
        }
      >
        <form id="register-serial-form" className="space-y-4" onSubmit={registerSerial}>
          <ProductSelect label="Product *" value={serialProductId} onChange={setSerialProductId} required />
          <Input
            label="Serial number *"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            required
          />
          <Input label="IMEI (optional)" value={serialImei} onChange={(e) => setSerialImei(e.target.value)} />
        </form>
      </Modal>
    </div>
  );
}
